// Shared vanilla CSS for the library button family (PrimaryButton, SecondaryButton,
// ApproveButton, CancelButton, CreateButton, DeleteButton).
// Prefix: btn-. Injected once in App.tsx (shared CSS — see STYLING.md).
//
// "Playful Frost" glass treatment: the primary CTA is the frosted-glass + aqua-glow
// pill (.btn-start — PrimaryButton & CreateButton ride it), a translucent frosted-glass
// secondary pill, solid approve/delete (with a mouse-following glow orb), and an
// opt-in animated beam.
// hover/active are gated with :not(.btn-disabled):not(.btn-loading) to preserve the
// original behavior where those states were suppressed while loading or disabled.
//
// Width: buttons hug their content by default (matching the design system's flat
// pf-btn). Opt into the product's 128px pill with the `fixedWidth` prop (.btn-w),
// or full width with `block` (.btn-block).
export const buttonStyles = `
  .btn-base {
    position: relative;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: var(--space-2);
    padding: 0 var(--space-5);
    white-space: nowrap;
    cursor: pointer;
    font-family: var(--font-sans);
    font-weight: var(--font-weight-bold);
    border: 1px solid transparent;
    border-radius: var(--rad-catchup-button);
    transition: background-color var(--duration-slow) var(--ease-standard),
                border-color var(--duration-slow) var(--ease-standard),
                color var(--duration-slow) var(--ease-standard),
                box-shadow var(--duration-slow) var(--ease-standard),
                transform var(--duration-base) var(--ease-standard);
  }
  .btn-base:focus-visible { outline: none; box-shadow: var(--shadow-input); }
  .btn-disabled { opacity: 0.5; cursor: default; pointer-events: none; }

  /* Icon + label row. Horizontal padding lives on .btn-base (the pill itself) so
     content-hug buttons keep their fill behind the padding, matching the design's
     flat pf-btn. */
  .btn-content {
    display: flex;
    flex-direction: row;
    justify-content: center;
    align-items: center;
    gap: var(--space-2);
    position: relative;
    z-index: 1;
  }
  .btn-content-plain {
    display: flex;
    flex-direction: row;
    justify-content: center;
    align-items: center;
    gap: var(--space-2);
    position: relative;
    z-index: 1;
  }

  /* Opt-in widths — default is content-hug (like the design's pf-btn).
     btn-w = the product's 128px pill (via the fixedWidth prop); btn-block = full width. */
  .btn-w { width: 8rem; }
  .btn-block { width: 100%; }
  /* Per-size height + font-size — exact port of the design pf-btn--sm/md/lg
     (32px·fs-base, 40px·fs-lg, 48px·fs-lg). */
  .btn-h-8 { height: 2rem; font-size: var(--fs-base); }
  .btn-h-10 { height: 2.5rem; font-size: var(--fs-lg); }
  .btn-h-12 { height: 3rem; font-size: var(--fs-lg); }
  .btn-h-14 { height: 3.5rem; font-size: var(--fs-lg); }

  /* SecondaryButton + CancelButton — translucent frosted-glass pill w/ aqua edge */
  .btn-secondary {
    position: relative;
    isolation: isolate;
    color: var(--catchup-blue-700);
    /* No backdrop-filter — renders per row in lists (e.g. the dashboard
       Homework widget); per-item blur layers starve tile raster on scroll.
       Near-opaque fill instead. */
    background-color: rgba(255, 255, 255, 0.82);
    border: 1.5px solid var(--catchup-blue-500);
    box-shadow:
      inset 0 1px 1px rgba(255, 255, 255, 0.55),
      inset 0 -6px 10px -8px rgba(15, 72, 89, 0.18),
      0 2px 6px rgba(15, 72, 89, 0.06);
  }
  .btn-secondary > * { position: relative; z-index: 1; }
  /* aqua highlight glow overlay (design pf-btn--secondary::after) — fades in on
     hover/active; the bare 8% is a gradient interpolation hint, kept 1:1 with the design */
  .btn-secondary::after {
    content: "";
    position: absolute;
    inset: 0;
    border-radius: inherit;
    pointer-events: none;
    z-index: -1;
    background: linear-gradient(0deg, #fff, var(--catchup-blue-500), rgba(84, 187, 203, 0.5), 8%, transparent);
    -webkit-mask-image: linear-gradient(0deg, #fff, transparent);
    mask-image: linear-gradient(0deg, #fff, transparent);
    opacity: 0;
    transition: opacity var(--duration-slow) var(--ease-standard);
  }
  .btn-secondary:not(.btn-disabled):not(.btn-loading):hover {
    color: var(--catchup-blue-600);
    /* Hover stays as opaque as rest — the old 0.46 fill only read as frosted
       through the removed blur; without it the button would go see-through
       over the colorful page bg. */
    background-color: rgba(255, 255, 255, 0.88);
    border-color: var(--catchup-blue-500);
    box-shadow:
      inset 0 1px 1px rgba(255, 255, 255, 0.7),
      inset 0 -8px 12px -8px rgba(84, 187, 203, 0.4),
      0 6px 16px rgba(84, 187, 203, 0.18);
  }
  .btn-secondary:not(.btn-disabled):not(.btn-loading):hover::after { opacity: 0.6; }
  .btn-secondary:not(.btn-disabled):not(.btn-loading):active { transform: scale(0.98); }
  .btn-secondary:not(.btn-disabled):not(.btn-loading):active::after { opacity: 0.9; }

  /* ApproveButton — solid green + mouse-following white glow orb */
  .btn-approve {
    position: relative;
    overflow: hidden;
    isolation: isolate;
    border-color: var(--catchup-green);
    background-color: var(--catchup-green);
    color: var(--catchup-white);
  }
  .btn-approve > * { position: relative; z-index: 1; }
  .btn-approve:not(.btn-disabled):not(.btn-loading):hover {
    background-color: var(--catchup-hover-green);
    border-color: var(--catchup-hover-green);
  }
  .btn-approve:not(.btn-disabled):not(.btn-loading):active { transform: scale(0.98); }

  /* DeleteButton — solid red + mouse-following white glow orb */
  .btn-delete {
    position: relative;
    overflow: hidden;
    isolation: isolate;
    border-color: var(--catchup-red);
    background-color: var(--catchup-red);
    color: var(--catchup-white);
  }
  .btn-delete > * { position: relative; z-index: 1; }
  .btn-delete:not(.btn-disabled):not(.btn-loading):hover {
    background-color: var(--catchup-white);
    color: var(--catchup-red);
    border-color: var(--catchup-red);
  }
  .btn-delete:not(.btn-disabled):not(.btn-loading):active { transform: scale(0.98); opacity: 0.9; }

  /* PrimaryButton + CreateButton — the primary CTA: frosted-glass + oscillating
     aqua glow (gamified). Built on .btn-base; overrides padding/fill/shadow and
     rides the .btn-glow-orb. */
  .btn-start {
    overflow: hidden;
    isolation: isolate;
    padding: 0 var(--space-8);
    color: var(--catchup-blue-600);
    /* No backdrop-filter — renders per row in lists (dashboard Homework
       widget); per-item blur layers starve tile raster on scroll. */
    background-color: rgba(255, 255, 255, 0.88);
    box-shadow:
      inset 0 0 0 1px rgba(84, 187, 203, 0.40),
      inset 2px 2px 1px -2px #fff,
      0 2px 6px rgba(15, 72, 89, 0.10),
      0 10px 24px rgba(15, 72, 89, 0.13);
  }
  .btn-start > * { position: relative; z-index: 1; }
  .btn-start:not(.btn-disabled):not(.btn-loading):hover {
    transform: translateY(-2px);
    box-shadow:
      0 0 0 1px rgba(84, 187, 203, 0.53),
      inset 2px 2px 1px -2px #fff,
      0 12px 30px rgba(84, 187, 203, 0.53);
  }
  .btn-start:not(.btn-disabled):not(.btn-loading):active { transform: translateY(0); }

  /* ── Glow orb — mouse-following aqua sparkle for start/approve/delete.
     z-index 0 so it sits behind the variant's  > *  content (approve/delete recolor
     it white below). Idle state oscillates; honors prefers-reduced-motion. */
  .btn-glow-orb {
    position: absolute;
    z-index: 0;
    width: 200px;
    height: 200px;
    border-radius: 50%;
    opacity: 0.5;
    pointer-events: none;
    background: radial-gradient(circle, #54bbcb 8%, transparent 68%);
    transition: transform 0.42s var(--ease-glass);
  }
  /* Orb rests at the idle animation's first frame; the "breathing" oscillation only
     runs while the button is hovered or keyboard-focused — not unconditionally — so it
     never animates off-screen / unattended (PrimaryButton renders this orb per row in
     lists like Homework). Mouse hover swaps to the mouse-following .btn-orb-hover state
     via useGlowOrb; :focus-within keeps the breathing affordance for keyboard users. */
  .btn-glow-orb.btn-orb-idle { transform: translate(-74%, -50%) scale(1.02); }
  .btn-base:hover .btn-glow-orb.btn-orb-idle,
  .btn-base:focus-within .btn-glow-orb.btn-orb-idle {
    animation: btn-glow-idle 2.6s ease-in-out infinite alternate;
  }
  @keyframes btn-glow-idle {
    from { transform: translate(-74%, -50%) scale(1.02); opacity: 0.5; }
    to   { transform: translate(-26%, -50%) scale(1.16); opacity: 0.66; }
  }
  .btn-approve .btn-glow-orb,
  .btn-delete .btn-glow-orb {
    background: radial-gradient(circle, rgba(255, 255, 255, 0.9) 6%, transparent 64%);
  }
  @media (prefers-reduced-motion: reduce) {
    .btn-glow-orb.btn-orb-idle,
    .btn-base:hover .btn-glow-orb.btn-orb-idle,
    .btn-base:focus-within .btn-glow-orb.btn-orb-idle {
      animation: none;
      transform: translate(-50%, -50%) scale(1);
    }
  }

  /* Animated border-beam — opt-in via the \`beam\` prop (.btn-beam). A light that
     travels around the frame; keeps the button's own fill + colors (design pf-btn--beam). */
  @property --btn-beam-angle { syntax: "<angle>"; inherits: false; initial-value: 0deg; }
  .btn-beam:not(.btn-disabled)::before {
    content: "";
    position: absolute;
    inset: 0;
    border-radius: inherit;
    padding: 1.5px;
    pointer-events: none;
    z-index: 0;
    background: conic-gradient(from var(--btn-beam-angle),
      transparent 0deg 232deg,
      color-mix(in srgb, var(--catchup-blue-500) 55%, transparent) 292deg,
      var(--catchup-blue) 328deg,
      #dcf4f8 346deg,
      transparent 360deg);
    -webkit-mask: linear-gradient(#000 0 0) content-box, linear-gradient(#000 0 0);
            mask: linear-gradient(#000 0 0) content-box, linear-gradient(#000 0 0);
    -webkit-mask-composite: xor;
            mask-composite: exclude;
    animation: btn-beam 2.8s linear infinite;
  }
  .btn-beam > * { position: relative; z-index: 1; }
  .btn-beam:not(.btn-disabled):not(.btn-loading):hover::before { animation-duration: 1.7s; }
  @keyframes btn-beam { to { --btn-beam-angle: 360deg; } }
  @media (prefers-reduced-motion: reduce) {
    .btn-beam::before { animation: none !important; }
  }
`;
